import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { ExternalLink } from "lucide-react";
import { AppShell } from "@/components/app-shell/AppShell";
import { PageHeader } from "@/components/common/PageHeader";
import { Panel, PanelHeader } from "@/components/common/Card";
import { CodeBlock } from "@/components/common/CodeBlock";
import { api } from "@/lib/api";

export const Route = createFileRoute("/integrations")({
  component: IntegrationsPage,
});

function IntegrationsPage() {
  const formsQ = useQuery({ queryKey: ["forms"], queryFn: () => api.listForms() });
  const sample = formsQ.data?.forms[0];
  const formKey = sample?.formKey ?? "REPLACE_WITH_YOUR_FORM_KEY";
  const origin = window.location.origin;

  return (
    <AppShell title="Integrations">
      <PageHeader
        eyebrow="integrations"
        title="Drop-in snippets"
        description={
          sample
            ? `Examples below pre-filled with ${sample.name} (${sample.formKey.slice(0, 12)}…).`
            : "Create a form first, then come back here for pre-filled snippets."
        }
      />

      <div className="grid grid-cols-12 gap-6">
        <Panel className="col-span-12 overflow-hidden lg:col-span-6">
          <PanelHeader
            title="Vanilla HTML"
            subtitle="Drop the script + form anywhere on your site"
          />
          <div className="space-y-3 p-6">
            <CodeBlock
              language="HTML"
              code={`<script src="${origin}/embed.js" defer></script>
<form data-mailapi="${formKey}">
  <label>Name <input name="name" required /></label>
  <label>Email <input name="email" type="email" required /></label>
  <label>Message <textarea name="message" required></textarea></label>
  <button type="submit">Send</button>
</form>`}
            />
            <p className="text-[11px] text-zinc-500">
              <code className="font-mono text-zinc-300">embed.js</code> injects
              the Turnstile widget and posts the form using JSON. No build step
              required.
            </p>
          </div>
        </Panel>

        <Panel className="col-span-12 overflow-hidden lg:col-span-6">
          <PanelHeader
            title="Fetch / API"
            subtitle="POST directly if you have your own UI"
          />
          <div className="space-y-3 p-6">
            <CodeBlock
              language="JS"
              code={`await fetch("${origin}/v1/forms/${formKey}/submit", {
  method: "POST",
  headers: { "Content-Type": "application/json" },
  body: JSON.stringify({
    name: data.name,
    email: data.email,
    message: data.message,
    "cf-turnstile-response": turnstileToken,
  }),
});`}
            />
            <p className="text-[11px] text-zinc-500">
              Always supply a Turnstile token; the Worker rejects submissions
              without one (<code className="font-mono">turnstile_fail</code>).
            </p>
          </div>
        </Panel>

        <Panel className="col-span-12 overflow-hidden">
          <PanelHeader title="cURL" subtitle="For quick smoke tests" />
          <div className="p-6">
            <CodeBlock
              language="bash"
              code={`curl -X POST "${origin}/v1/forms/${formKey}/submit" \\
  -H "Content-Type: application/json" \\
  -H "Origin: https://yoursite.com" \\
  -d '{"name":"Test","email":"test@example.com","message":"hi","cf-turnstile-response":"TOKEN"}'`}
            />
          </div>
        </Panel>

        <Panel className="col-span-12 p-6">
          <h3 className="mb-3 font-display text-sm font-semibold text-zinc-100">
            Documentation
          </h3>
          <ul className="space-y-2 text-sm text-zinc-300">
            <DocLink href="https://developers.cloudflare.com/turnstile/get-started/" label="Cloudflare Turnstile setup" />
            <DocLink href="https://documentation.mailgun.com/docs/mailgun/" label="Mailgun API reference" />
          </ul>
        </Panel>
      </div>
    </AppShell>
  );
}

function DocLink({ href, label }: { href: string; label: string }) {
  return (
    <li>
      <a
        href={href}
        target="_blank"
        rel="noreferrer noopener"
        className="inline-flex items-center gap-2 text-zinc-300 hover:text-ember"
      >
        {label}
        <ExternalLink className="h-3 w-3" />
      </a>
    </li>
  );
}
